import { KpiStrip, Metric } from "@/design-system/Metric";
import { Panel } from "@/design-system/Panel";
import type { AccountAnalyticsEvidence } from "@/shared/analytics/presentation";
import type { AccountAnalyticsResponse } from "@/shared/api";
import { isZero, signedMoney, signedPercent } from "@/shared/money/money";

function day(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC",
  });
}

/** «25 июл. 2026 г., 00:00 — 24 авг. 2026 г., 12:00 UTC». */
function periodLabel(analytics: AccountAnalyticsResponse): string {
  const since = analytics.effective_since == null ? "—" : day(analytics.effective_since);
  return `${since} — ${day(analytics.effective_until)} UTC`;
}

export function AnalyticsSummary({
  analytics,
  evidence,
}: {
  readonly analytics: AccountAnalyticsResponse;
  readonly evidence: AccountAnalyticsEvidence;
}): React.JSX.Element {
  const portfolioPnl = evidence.portfolioPnl;
  const ownerReturn = evidence.ownerReturn;
  const drawdown = evidence.maximumDrawdown;
  // The server records damage as a non-negative magnitude. Direction is a UI
  // semantic here: any non-zero drawdown is adverse, never a gain.
  const adverseDrawdown =
    drawdown === null || isZero(drawdown)
      ? drawdown
      : drawdown.startsWith("-")
        ? drawdown
        : `-${drawdown.replace(/^\+/, "")}`;

  return (
    <Panel title="Результат за полный период" testId="analytics-summary" variant="analytics">
      <p
        className="mb-3 text-helper-sm text-text-muted"
        data-testid="analytics-period"
      >
        {periodLabel(analytics)}
      </p>
      <KpiStrip columns={5}>
        <Metric
          metricId="pnl.realised_net"
          role="primary"
          value={evidence.realisedNet}
          directional
          display={signedMoney(evidence.realisedNet)}
          hint="наблюдаемый итог после комиссий и funding"
          size="large"
        />
        <Metric
          label="Общий PnL портфеля"
          value={portfolioPnl}
          directional
          display={portfolioPnl === null ? undefined : signedMoney(portfolioPnl)}
          hint="из серверной кривой капитала владельца"
          size="large"
          testId="analytics-portfolio-pnl"
        />
        <Metric
          metricId="analytics.owner_return"
          role="primary"
          value={ownerReturn}
          directional
          display={ownerReturn === null ? undefined : signedPercent(ownerReturn)}
          hint="к капиталу владельца, взвешенному по времени"
        />
        <Metric
          label="Максимальная просадка"
          value={adverseDrawdown}
          directional
          display={adverseDrawdown === null ? undefined : signedPercent(adverseDrawdown)}
          hint="от пика капитала владельца"
          testId="analytics-drawdown"
        />
        <Metric
          metricId="analytics.closed_cycles"
          role="primary"
          value={String(evidence.cycles)}
          display={String(evidence.cycles)}
          hint={`${String(evidence.winners)} в плюс · ${String(evidence.losers)} в минус`}
        />
      </KpiStrip>
    </Panel>
  );
}
